import { useCallback } from 'react'
import { createRoute, Outlet, useLocation } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useSessionWatcher } from '@/application/hooks/useSessionWatcher'
import { usePageLangAndHreflang } from '@/application/hooks/useDocumentMeta'
import { usePullToRefresh } from '@/application/pwa/usePullToRefresh'
import { rootRoute } from '@/ui/pages/root'
import { PublicNavbar } from '@/ui/components/public/Navbar'
import { PublicFooter } from '@/ui/components/public/Footer'
import { CookieConsentBanner } from '@/ui/components/common/CookieConsentBanner'
import { InstallAppPrompt } from '@/ui/components/common/InstallAppPrompt'
import { PullToRefreshIndicator } from '@/ui/components/common/PullToRefreshIndicator'
import { PublicJsonLd } from '@/ui/components/public/PublicJsonLd'
import { ScrollToTopButton } from '@/ui/components/public/ScrollToTopButton'

export const publicLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'public-layout',
  component: PublicLayout,
})

export function PublicLayout() {
  useSessionWatcher(() => {
    window.location.reload()
  })

  const location = useLocation()
  usePageLangAndHreflang(location.pathname)

  const queryClient = useQueryClient()
  // Refetch the data the current screen is actually showing rather than
  // reloading the document: a full reload would throw away the SPA and
  // re-download the whole bundle for what is a data-freshness gesture.
  const handleRefresh = useCallback(
    () => queryClient.refetchQueries({ type: 'active' }),
    [queryClient],
  )
  const { pullDistance, isRefreshing } = usePullToRefresh({ onRefresh: handleRefresh })

  // The shell is always normal document flow: the whole page scrolls as one,
  // with the header staying in view via `sticky top-0` and the footer simply
  // ending the document.
  return (
    <div className="bg-background flex flex-col min-h-screen-safe print:h-auto print:min-h-0 print:overflow-visible">
      <PublicJsonLd />
      <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={isRefreshing} />
      <div className="print:hidden">
        <PublicNavbar />
      </div>
      <main
        id="main-content"
        className="flex-1 flex flex-col overflow-x-guard scroll-pt-16 print:overflow-visible"
      >
        <Outlet />
      </main>
      <div className="print:hidden">
        <PublicFooter />
      </div>
      <div className="print:hidden">
        <CookieConsentBanner />
        <InstallAppPrompt />
      </div>
      <ScrollToTopButton />
    </div>
  )
}
